import { Button } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import type { NbaItem } from "../redux/portfolio.types"
import { presentationFor, recordNameFrom } from "./nbaPresentation.ts"
import { homeColor, homePastel, homeText } from "../home-theme.ts"
import { HomeCard, type HomeCardEdit } from "./HomeCard.tsx"
import {
  ArrowRightIcon,
  CircleAlertIcon,
  ClockIcon,
  DollarSignIcon,
  ClipboardListIcon
} from "./home-icons.tsx"

/** How many cards sit side by side on desktop, per the mockup. */
export const PRIORITY_ACTION_LIMIT = 3

type Glyph = React.FC<React.SVGProps<SVGSVGElement>>

type Tone = "critical" | "warn" | "info"

// Pastel by urgency: rose, peach, sky. Still three distinct hues, so a
// colour-blind reader is not the only one who needs the words.
const TONE: Record<Tone, { fg: string; bg: string }> = {
  critical: homePastel.rose,
  warn: homePastel.peach,
  info: homePastel.sky
}

/**
 * The glyph a card leads with, chosen by what the action is ABOUT rather than
 * how urgent it is — money, a date, or paperwork. Severity is carried by the
 * tone instead, so the two say different things.
 */
const GLYPH_BY_KIND: Record<string, Glyph> = {
  money: DollarSignIcon,
  schedule: ClockIcon,
  lead: ArrowRightIcon,
  compliance: ClipboardListIcon,
  budget: CircleAlertIcon
}

/**
 * The server already ranks the feed and builds one item per kind, most urgent
 * kind first (`2026-09-30-the-nba-card-renders-the-servers-order`). Taking the
 * first three preserves that order — re-sorting here by severity is exactly
 * the bug that entry records, where five critical items hid every warn one.
 */
export const topPriorityActions = (
  items: readonly NbaItem[] | null | undefined,
  limit = PRIORITY_ACTION_LIMIT
): NbaItem[] => (items ?? []).slice(0, limit)

const toneOf = (item: NbaItem): Tone =>
  item.severity === "critical" || item.severity === "warn"
    ? item.severity
    : "info"

/** The action's destination, when the server sent a navigable one. */
export const destinationOf = (item: NbaItem): string | null => {
  const url = item.action?.params?.url
  return typeof url === "string" && url.length > 0 ? url : null
}

const ActionCard = ({ item }: { item: NbaItem }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const tone = TONE[toneOf(item)]
  // The issue leads; the record's own name moves to the context line. A kind
  // this build has not met keeps the server's sentence rather than vanishing.
  const presentation = presentationFor(item.kind)
  const Glyph = presentation
    ? (GLYPH_BY_KIND[presentation.glyph] ?? CircleAlertIcon)
    : CircleAlertIcon
  const heading = presentation
    ? t(presentation.titleKey, presentation.title)
    : item.title
  const record = presentation ? recordNameFrom(item.title) : null
  const actionLabel = presentation
    ? t(presentation.actionKey, presentation.action)
    : t("home.today.reviewAction", "Review")
  const to = destinationOf(item)

  return (
    <article className="home-priority-actions__item">
      <span
        aria-hidden
        style={{
          background: tone.bg,
          color: tone.fg,
          borderRadius: 8,
          width: 36,
          height: 36,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0
        }}
      >
        <Glyph width={18} height={18} />
      </span>
      <div
        className="min-w-0"
        style={{ display: "flex", flexDirection: "column", gap: 2 }}
      >
        <span style={{ ...homeText.actionTitle, color: tone.fg }}>
          {heading}
        </span>
        {record ? (
          <span
            style={{ ...homeText.meta, color: homeColor.ink }}
            className="truncate"
          >
            {record}
          </span>
        ) : null}
        {item.rationale ? (
          <span style={{ ...homeText.meta, color: homeColor.inkMuted }}>
            {item.rationale}
          </span>
        ) : null}
        {to ? (
          <Button
            type="link"
            size="small"
            style={{
              padding: 0,
              height: "auto",
              alignSelf: "flex-start",
              ...homeText.button
            }}
            onClick={() => navigate(to)}
          >
            {actionLabel} →
          </Button>
        ) : null}
      </div>
    </article>
  )
}

/**
 * Priority actions as ONE widget: the shared header carries its title and its
 * single View all link, and up to three of the server's prioritised items sit
 * in the body divided by hairlines. The link used to appear twice — beside a
 * section heading above the cards and again under them.
 */
export const HomePriorityActions = ({
  items,
  total,
  onViewAll,
  title,
  edit
}: {
  items: readonly NbaItem[] | null | undefined
  /** Everything the server has, so "View all" can say what it opens. */
  total?: number
  onViewAll?: () => void
  /** A user's own name for the widget. */
  title?: string
  edit?: HomeCardEdit | null
}) => {
  const { t } = useTranslation()
  const top = topPriorityActions(items)
  const count = typeof total === "number" ? total : top.length

  return (
    <HomeCard
      testId="home-priority-actions"
      edit={edit}
      bodyPadding={0}
      icon={<CircleAlertIcon width={14} height={14} />}
      title={title ?? t("home.unit.priorityActions", "Priority actions")}
      actions={
        onViewAll && count > 0 ? (
          <Button
            type="link"
            size="small"
            style={{ padding: 0 }}
            onClick={onViewAll}
            data-testid="home-view-all-actions"
          >
            {t("home.today.viewAllActions", "View all actions ({{count}})", {
              count
            })}
          </Button>
        ) : null
      }
    >
      {top.length ? (
        <div className="home-priority-actions">
          {top.map((item) => (
            <ActionCard key={item.id} item={item} />
          ))}
        </div>
      ) : (
        <p
          style={{
            ...homeText.body,
            color: homeColor.onTrackInk,
            margin: 0,
            padding: 16
          }}
        >
          {t("home.today.allClear", "Nothing needs your attention right now.")}
        </p>
      )}
    </HomeCard>
  )
}
